/**
 * GlobalState.config 切片（任务 8d）：`config/get` 的主线程只读镜像。
 * 当前消费方是展示层拼媒体 URL（resolveMediaUrl 要 cdn）；captchaAppId 等
 * 登录页用途仍走各自的 RPC，不读本切片。
 *
 * 请求口径（requests §3 注 7）：登录成功返回前 Worker 已缓存 config——这里的
 * config/get 必然缓存命中**零 HTTP**，「二次启动首屏前零请求」不变量保持。
 * 失败保持 null（头像等退回占位态），崩溃重启 resync 再拉。
 * 登出清 null：路线切换（14.39 若保留）或换环境后不残留旧 cdn。
 *
 * 会话期内的更新走 'config/changed' 推送（21ah）：Worker 的 config 缓存原先既
 * 没 TTL 也没有变更外抛，运维改了 Cdn/Ws，老客户端要么永远不知道、要么刷新了
 * Worker 缓存但主线程这份还是旧的（切线路后图片仍打旧 CDN 就是这么来的）。
 */

import type { GlobalState, GlobalStore } from './global';
import type { AppWorkerHost } from './authState';
import type { AppConfig } from '~/worker/contract';

/** null = 未加载（未登录/未回包） */
export type ConfigSlice = AppConfig | null;

export const CONFIG_INITIAL: ConfigSlice = null;

export interface ConfigConnection {
  /** Worker 崩溃重建后重拉（内存态丢失，kv 缓存仍在——依旧零 HTTP） */
  resync(): void;
  dispose(): void;
}

export function connectConfig(store: GlobalStore, host: AppWorkerHost): ConfigConnection {
  let epoch = 0;
  let loaded = false;

  function load(): void {
    const seq = ++epoch;
    void host.call('config/get', null).then(
      (cfg) => {
        if (seq === epoch) store.set({ config: cfg });
      },
      () => undefined, // 失败保持现值；resync 兜底
    );
  }

  // 配置变更推送（21ah）：Worker 侧后台软刷新/切线路/设置页重取，**值真变了**
  // 才来一条。整体替换切片——cdn 一换，头像等 resolveMediaUrl 的结果立刻跟着走。
  const offPush = host.onPush('config/changed', (cfg) => {
    loaded = true;
    epoch += 1; // 在途的 config/get 响应作废（它拿的是旧值）
    store.set({ config: cfg });
  });

  function onAuth(auth: GlobalState['auth']): void {
    if (auth.status === 'logged-in') {
      if (!loaded) {
        loaded = true;
        load();
      }
    } else if (auth.status === 'logged-out') {
      loaded = false;
      epoch += 1; // 在途响应过期
      store.set({ config: CONFIG_INITIAL });
    }
  }
  const offAuth = store.subscribe((s) => s.auth, onAuth);
  onAuth(store.get().auth);

  return {
    resync() {
      if (loaded) load();
    },
    dispose() {
      offPush();
      offAuth();
    },
  };
}
